export type WellLogCurveName =
  | 'md'
  | 'tvdss'
  | 'gr'
  | 'cali'
  | 'rhob'
  | 'resd'
  | 'phie'
  | 'phit'
  | 'swt'
  | 'vsh'
  | 'perm';

export type WellLogCurves = Partial<
  Record<WellLogCurveName, (number | null)[]>
>;

/**
 * The calibration runs over two independent datasets, mirroring the two
 * calibration sheets of the reference workbook: continuous well logs
 * (compared against SWT) and sparse core/RCAL plugs (compared against
 * lab Sw). Each keeps its own FWL, gradient and parameter fits.
 */
export type SwHeightDatasetKind = 'log' | 'core';

export interface WellLogs {
  project_uid: string;
  kind: SwHeightDatasetKind;
  curves: WellLogCurves;
  row_count: number;
  source_filename: string | null;
  warnings: string[];
}

export type SwHeightEquationType =
  | 'linear'
  | 'power'
  | 'logarithmic'
  | 'exponential';

export interface SwHeightParameterPair {
  p1: number;
  p2: number;
  equation_type: SwHeightEquationType;
  source: 'tendency_fit' | 'manual';
}

/**
 * Bounds applied to the fitted Swirr, Pe and lambda, plus the reservoir
 * cut-off. Defaults reproduce the reference workbook.
 *
 * `pe_min` and `lambda_min` must stay strictly positive: Pe sits in a
 * denominator, and the exponent is `-1/lambda`, so a non-positive lambda
 * flips its sign and inverts the model.
 */
export interface SwHeightClamps {
  swirr_min: number;
  swirr_max: number;
  pe_min: number;
  lambda_min: number;
  perm_cutoff: number;
}

export const SW_HEIGHT_CLAMP_DEFAULTS: SwHeightClamps = {
  swirr_min: 0.04,
  swirr_max: 0.7,
  pe_min: 0.5,
  lambda_min: 0.04,
  perm_cutoff: 0.1
};

/**
 * Which capillary-pressure model turns Pc into Sw.
 *
 * Brooks-Corey: `Sw = Swirr + (1 − Swirr)·(Pc/Pe)^(−1/λ)`.
 * Liao: the closed-form inverse of the app's own forward model, so this
 * page and the Kr × Pc curves agree. Liao reads Pe as its entry pressure
 * and derives λ from `best_a`, `best_b` and Pe, so it has no λ input.
 *
 * The choice drives *both* saturation curves — the one from the tendency
 * fits and the one from the per-rock-type parameters.
 */
export type SwHeightMethod = 'brooks_corey' | 'liao';

/**
 * Already-evaluated parameters for one rock type — what drives
 * `SwHeightResult.sw_by_rock_type`. The correlation workflow evaluates
 * each tendency at the zone's mean, so there is one number per parameter
 * instead of a curve to sample per row.
 */
export interface SwHeightRockTypeParams {
  swirr: number | null;
  pe: number | null;
  lambda_value: number | null;
  best_a: number | null;
  best_b: number | null;
  true_pc_max: number | null;
  source: 'zone_values' | 'manual';
}

export interface SwHeightSettings {
  project_uid: string;
  kind: SwHeightDatasetKind;
  fwl: number | null;
  grad_diff: number | null;
  x_axis: 'rqi' | 'fzi';
  swirr_params: SwHeightParameterPair | null;
  pe_params: SwHeightParameterPair | null;
  lambda_params: SwHeightParameterPair | null;
  /** Liao only. Swirr and Pe are shared with Brooks-Corey. */
  best_a_params: SwHeightParameterPair | null;
  best_b_params: SwHeightParameterPair | null;
  true_pc_max_params: SwHeightParameterPair | null;
  method: SwHeightMethod;
  /** Manual per-rock-type edits, keyed by zone uid. */
  rock_type_overrides: Record<string, SwHeightRockTypeParams>;
  clamps: SwHeightClamps;
}

export type SwHeightIssueSeverity = 'blocking' | 'warning' | 'info';

export interface SwHeightIssue {
  code: string;
  severity: SwHeightIssueSeverity;
  message: string;
  row_count: number;
  example_rows: number[];
}

export interface SwHeightValidation {
  issues: SwHeightIssue[];
  rows_total: number;
  rows_with_sw: number;
}

/** Agreement between calculated Sw and the measured reference curve. */
export interface SwHeightComparison {
  n: number;
  mae: number | null;
  rmse: number | null;
  /** Mean signed error; positive means the model is too wet. */
  bias: number | null;
  r_squared: number | null;
  reference_curve: string | null;
}

export interface SwHeightResult {
  rqi: (number | null)[];
  phiz: (number | null)[];
  fzi: (number | null)[];
  rock_type: (number | null)[];
  hafwl: (number | null)[];
  pc: (number | null)[];
  /** These four describe the `sw` curve: one value per row. */
  swirr: (number | null)[];
  pe: (number | null)[];
  /** Derived, not fitted, under Liao — null where it came out invalid. */
  lambda_value: (number | null)[];
  best_a: (number | null)[];
  best_b: (number | null)[];
  true_pc_max: (number | null)[];
  /** The saturation from the tendency fits sampled at each row. */
  sw: (number | null)[];
  /**
   * The same model driven by one parameter set per rock type, computed
   * alongside `sw` rather than instead of it so the two can be read
   * against each other. Null wherever the row's rock type has no
   * parameters — including rock type 0.
   */
  sw_by_rock_type: (number | null)[];
  parameters_used: Record<string, SwHeightParameterPair | null>;
  /** What drove `sw_by_rock_type`, keyed by rock-type index as a string. */
  rock_type_params_used: Record<string, SwHeightRockTypeParams>;
  fwl_used: number | null;
  grad_diff_used: number | null;
  x_axis_used: 'rqi' | 'fzi';
  kind: SwHeightDatasetKind;
  method_used: SwHeightMethod;
  clamps_used: SwHeightClamps;
  validation: SwHeightValidation;
  comparison: SwHeightComparison;
}

export interface SwHeightZone {
  uid: string;
  start: number;
  end: number;
  color: string | null;
  /** FZI midpoint the correlation evaluated its tendencies at. */
  mean?: number | null;
  /**
   * This rock type's resolved parameters: the correlation's zone values
   * with any manual override on top. Null when nothing is available, in
   * which case the per-rock-type curve has a gap over these rows.
   */
  params?: SwHeightRockTypeParams | null;
}

export interface SwHeightDefaults {
  x_axis: 'rqi' | 'fzi';
  swirr: SwHeightParameterPair | null;
  pe: SwHeightParameterPair | null;
  lambda: SwHeightParameterPair | null;
  best_a?: SwHeightParameterPair | null;
  best_b?: SwHeightParameterPair | null;
  true_pc_max?: SwHeightParameterPair | null;
  grad_diff: number;
  clamps: SwHeightClamps;
}

export interface SwHeightSample {
  sample_uid: string;
  sample_code: string | null;
  well_uid: string | null;
  depth: number;
  porosity: number | null;
  permeability: number | null;
  rqi: number | null;
  fzi: number | null;
  rock_type: number | null;
}

export interface SwHeightData {
  project_uid: string;
  kind: SwHeightDatasetKind;
  /** Dataset kinds that currently hold data for this project. */
  available_datasets: SwHeightDatasetKind[];
  logs: WellLogs;
  settings: SwHeightSettings;
  /**
   * Whether `settings` was ever saved, or is only the defaults resolved from
   * the project's correlation. The two are indistinguishable from `settings`
   * alone -- same shape, same fields -- and the page needs to tell them apart:
   * it decides whether the first compute persists itself, and it is the
   * baseline the unsaved-changes indicator is measured against.
   */
  has_saved_settings: boolean;
  defaults: SwHeightDefaults;
  zones: SwHeightZone[];
  samples: SwHeightSample[];
  result: SwHeightResult;
}

export interface SwHeightComputeResponseContent {
  project_uid: string;
  kind: SwHeightDatasetKind;
  settings: SwHeightSettings;
  result: SwHeightResult;
}

/**
 * Calibration constants found stored beside the table in the uploaded
 * file (the reference log tab keeps FWL and the gradient difference
 * there). Advisory only — never applied without the user opting in.
 */
export interface SwHeightSuggestedSettings {
  fwl?: number;
  grad_diff?: number;
}

export interface WellLogImportPreview {
  project_uid: string;
  kind: SwHeightDatasetKind;
  curves: WellLogCurves;
  row_count: number;
  detected_columns: Record<string, string>;
  /** Non-empty header cells that matched no known curve. */
  ignored_columns: string[];
  /** Unit group resolved per curve from the units row, when present. */
  units: Record<string, string>;
  suggested_settings: SwHeightSuggestedSettings;
  /** Which worksheet the importer read. */
  sheet_name: string | null;
  available_sheets: string[];
  warnings: string[];
  preview_rows: Record<string, number | null>[];
  filename: string | null;
}

export interface SwHeightSeedResult {
  project_uid: string;
  kind: SwHeightDatasetKind;
  row_count: number;
  curve_names: string[];
  warnings: string[];
}

export interface SwHeightComputePayload {
  fwl?: number | null;
  grad_diff?: number | null;
  x_axis?: 'rqi' | 'fzi';
  swirr_params?: SwHeightParameterPair | null;
  pe_params?: SwHeightParameterPair | null;
  lambda_params?: SwHeightParameterPair | null;
  best_a_params?: SwHeightParameterPair | null;
  best_b_params?: SwHeightParameterPair | null;
  true_pc_max_params?: SwHeightParameterPair | null;
  method?: SwHeightMethod;
  rock_type_overrides?: Record<string, SwHeightRockTypeParams>;
  clamps?: SwHeightClamps;
  persist_settings?: boolean;
}

/**
 * Every editable settings field, so a recompute can never omit one.
 *
 * The compute response's `settings` replaces local state wholesale, so a
 * field left out of the payload comes back at its stored value and the
 * user's edit silently reverts. Deriving the payload from this list is
 * what stops that happening again.
 */
export const SW_HEIGHT_EDITABLE_FIELDS = [
  'fwl',
  'grad_diff',
  'x_axis',
  'swirr_params',
  'pe_params',
  'lambda_params',
  'best_a_params',
  'best_b_params',
  'true_pc_max_params',
  'method',
  'rock_type_overrides',
  'clamps'
] as const;

export type SwHeightEditableField =
  (typeof SW_HEIGHT_EDITABLE_FIELDS)[number];

/** Narrow the settings down to exactly the fields compute accepts. */
export const toComputePayload = (
  settings: SwHeightSettings
): SwHeightComputePayload =>
  SW_HEIGHT_EDITABLE_FIELDS.reduce<SwHeightComputePayload>(
    (payload, field) => ({ ...payload, [field]: settings[field] }),
    {}
  );

export interface SwHeightApiResponse<T> {
  message: string;
  content: T | null;
}
